import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useCopy } from "@/lib/copyVariant";
import { img } from "@/lib/img";

export default function NotJustADeckSection() {
  const { c } = useCopy();
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      className="py-16 sm:py-24 md:py-32 px-5 md:px-10 text-center relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #fff8f3 0%, #f3ece4 50%, #fff8f3 100%)" }}
      data-testid="not-just-a-deck-section"
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at top, rgba(150,174,180,0.18) 0%, transparent 60%)" }} />

      {/* Floating animated elements */}
      <motion.div className="absolute top-20 left-[8%] w-12 h-12 rounded-full pointer-events-none hidden md:block"
        style={{ background: "radial-gradient(circle, rgba(166,193,168,0.45), transparent 70%)" }}
        animate={{ y: [0, -20, 0], rotate: [0, 90, 0] }} transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }} />
      <motion.div className="absolute bottom-20 right-[10%] w-16 h-16 rounded-full pointer-events-none hidden md:block"
        style={{ background: "radial-gradient(circle, rgba(150,174,180,0.4), transparent 70%)" }}
        animate={{ y: [0, 22, 0], x: [0, -10, 0] }} transition={{ repeat: Infinity, duration: 9, ease: "easeInOut" }} />
      <motion.div className="absolute top-1/3 right-[14%] w-2 h-2 rounded-full bg-[#a89b94]/40 pointer-events-none hidden lg:block"
        animate={{ scale: [1, 1.8, 1], opacity: [0.4, 0.9, 0.4] }} transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }} />
      <motion.div className="absolute bottom-1/3 left-[12%] w-2 h-2 rounded-full bg-[#96aeb4]/50 pointer-events-none hidden lg:block"
        animate={{ scale: [1, 2, 1], opacity: [0.5, 1, 0.5] }} transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: 1 }} />

      <div className="max-w-3xl mx-auto relative">
        {/* Product mockup with float + glow */}
        <motion.div className="relative inline-block mb-8 sm:mb-12"
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}>
          <motion.div className="absolute -inset-8 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(166,193,168,0.35) 0%, transparent 65%)" }}
            animate={{ scale: [1, 1.1, 1], opacity: [0.6, 0.9, 0.6] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }} />
          <motion.img
            src={img("https://customer-assets.emergentagent.com/job_calma-sutras/artifacts/c6g3item_tent%20card%20Calmasutras-2.png", { w: 640 })}
            alt="Calmasutras deck and box" loading="lazy" decoding="async" width="320" height="380"
            className="relative w-48 sm:w-64 md:w-80 mx-auto rounded-2xl"
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
          />
        </motion.div>

        <motion.h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#464646] leading-[1.15] tracking-tight mb-4"
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ duration: 0.7 }}>
          {c("notJustHeadingL1")}
          <br />
          <span className="text-gradient-sage">{c("notJustHeadingL2")}</span>
        </motion.h2>
        <motion.p className="text-xs sm:text-sm text-[#464646]/60 max-w-md mx-auto mb-8"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.2, duration: 0.7 }}>
          {c("notJustBody")}
        </motion.p>
        <motion.div className="flex flex-col sm:flex-row gap-3 justify-center"
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }}>
          <button onClick={() => scrollTo("#begin-journey")} data-testid="mid-cta-bundle-btn"
            className="bg-[#464646] text-white rounded-full px-7 py-3.5 text-sm font-semibold hover:bg-[#96aeb4] transition-all shadow-md shadow-[#464646]/10 inline-flex items-center justify-center gap-2">
            {c("notJustCta1")} <ArrowRight size={15} /></button>
          <button onClick={() => scrollTo("#begin-journey")} data-testid="mid-cta-deck-btn"
            className="border border-[#464646]/20 bg-white text-[#464646]/80 hover:text-[#464646] hover:border-[#464646]/40 rounded-full px-7 py-3.5 text-sm font-semibold transition-all w-full sm:w-auto">
            {c("notJustCta2")}</button>
        </motion.div>
      </div>
    </section>
  );
}
